/**
 * The ways a person reaches one policy task, and the places a PDF is read from.
 *
 * HumanFirewall is a human-risk-management and security-awareness-training platform; the
 * Policies module puts an organisation's own policy PDFs in front of its own staff.
 *
 * An employee reaches a task through the portal (signed in, the portal JWT) or through the
 * link in an e-mail (no sign-in; the signature is the credential). Everything the flow does
 * is identical behind both doors, so the hooks (portal-hooks.ts `usePolicyTaskDoor`) and the
 * PDF renderer (components/policies/pdf) take a door and never ask which one it is.
 *
 * v3 (C1, C2) adds the two PRACTICE doors, so a maker can walk the very same flow without
 * recording anything: `preview` (the admin's full-screen "Preview as employee", admin
 * session) and `test` (the link in a "send a test to myself" e-mail, no sign-in). The flow
 * components are shared with the live doors and never forked: the hooks route a practice
 * door to the sandbox (lib/policies/sandbox-session.ts: progress lives in the browser) and
 * to the stateless server calls (attempts, grade, sample receipt). A practice door never
 * writes a row.
 */

/** One task behind the portal door (the portal JWT) or the signed-link door (no bearer). */
export type PolicyLiveDoor =
  | { kind: 'portal'; attestationId: string }
  | { kind: 'signed'; attestationId: string; sig: string }

/**
 * v3 (C1, C2): a practice flow. `preview` = the admin's "Preview as employee" of one version
 * (admin session + x-workspace-id); `test` = a test link (the signed test-run id is the credential).
 */
export type PolicyPracticeDoor =
  | { kind: 'preview'; policyId: string; versionId: string; language?: string | null }
  | { kind: 'test'; testId: string; sig: string }

/** Every door the flow can be opened behind. */
export type PolicyTaskDoor = PolicyLiveDoor | PolicyPracticeDoor

/** A practice door records nothing: progress is kept in the browser. */
export function isPracticeDoor(door: PolicyTaskDoor): door is PolicyPracticeDoor {
  return door.kind === 'preview' || door.kind === 'test'
}

/**
 * Where a PDF is read from: an admin looking at a version (admin session +
 * x-workspace-id), an employee's task in the portal, or a task behind a signed link.
 * The renderer (components/policies/pdf) loads the bytes through `usePolicyFileLoader`
 * and never builds an address or a header itself.
 *
 * v3 (H4): `second` is the translated file shown beside the base one, read from the same
 * door in its language (`?language=`); behind a preview it is the language pack's admin file.
 *
 * v3 (G3): `auditor` is one released version read by an invited outside auditor, through the
 * auditor's own door (`X-Policy-Audit-Token`, never the admin session; lib/policies/audit-fetch.ts).
 */
export type PolicyPdfSource =
  | { kind: 'admin'; policyId: string; versionId: string }
  | { kind: 'adminPack'; policyId: string; packId: string }
  | { kind: 'second'; door: PolicyTaskDoor; language: string; packId: string }
  | { kind: 'auditor'; policyId: string; versionId: string }
  | PolicyTaskDoor

/** A stable string for a source: the key of a module-level document cache, a React key. */
export function policyPdfSourceId(source: PolicyPdfSource): string {
  switch (source.kind) {
    case 'admin':
      return `admin:${source.policyId}:${source.versionId}`
    case 'adminPack':
      return `admin-pack:${source.policyId}:${source.packId}`
    case 'auditor':
      return `auditor:${source.policyId}:${source.versionId}`
    case 'portal':
      return `portal:${source.attestationId}`
    case 'signed':
      return `signed:${source.attestationId}:${source.sig}`
    case 'preview':
      return `preview:${source.policyId}:${source.versionId}${source.language ? `:${source.language}` : ''}`
    case 'test':
      return `test:${source.testId}:${source.sig}`
    case 'second':
      return `second:${source.language}:${source.packId}:${policyPdfSourceId(source.door)}`
  }
}

/** A stable string for a door: the key of its sandbox session. */
export function policyDoorId(door: PolicyTaskDoor): string {
  return policyPdfSourceId(door)
}

/** The bytes of a policy PDF as the API sent them. */
export interface PolicyPdfBytes {
  bytes: ArrayBuffer
  fileName: string
}
